import {
  model,
  Model,
  prop,
  _async,
  _await,
  idProp,
  registerRootStore,
  modelAction,
  modelFlow,
  Frozen,
  frozen,
  rootRef,
  detach,
  findParent,
  getSnapshot,
} from "mobx-keystone";
import { computed, observable } from "mobx";
import { requestPayServiceParams, utils } from "lnurl-pay";

import {
  sendToBobController,
  sendToRailsController,
} from "../../helpers/requests";
import { notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { decode } from "@gandlaf21/bolt11-decode";
import {
  classifyLightningInput,
  InvoiceSummary,
  summariseBolt11Invoice,
  uint8ArrayToHex,
} from "../helpers/payAttemptHelper";
import { Bob } from "./Bob";
import { LnUrlOneResponse, validateLnUrlOneResponse } from "../helpers/lnUrl";
import { PlatformPlugin } from "webpack";
import { setMaxRoutingFeeAutomatically } from "../helpers/routingFees";
import { CurrencySnapshot } from "../helpers/types";
import { makeSpreadsheetLine } from "../helpers/spreadSheet";
import { makeTransactionsListLine } from "../helpers/transactionsList";
import { makeTransactionsPage } from "../helpers/transactionViewer";
import { BobsWorkspace } from "./BobsWorkspace";

export type PayAttemptStatus =
  | "user-trying-to-specify"
  | "needs-user-input-for-lnurl-stage-two"
  | "needs-pay-amount"
  | "ready-to-attempt"
  | "created"
  | "has_failed"
  | "has_succeeded";

export const STATUSES_SHOWING_SERVER_PERSISTENCE: PayAttemptStatus[] = [
  "created",
  "has_failed",
  "has_succeeded",
];

// export type SimplifiedStatus = "created" | "has_failed" | "has_succeeded";

export interface BobPayattemptFromServer {
  uuid: string;
  pay_request: string;
  attempt_status: PayAttemptStatus;
  satoshis_amount: number;
  max_routing_fee_satoshis: number;
  platform_fee_satoshis: number;
  routing_fees_paid_satoshis: number;
  created_at_timestamp: number; // ← new (ms since epoch)
  payment_failure_error_message?: string; // ← new
  comment: string | null; // ← new
  currency_snapshot: CurrencySnapshot; // ← new
  lightning_address: string | null; // ← new
  zap_picture_url: string | null; // ← new
  zap_name: string | null; // ← new
}

const ERROR_MESSAGES = {
  INVOICE_NOT_YET_VALID: "Please enter a valid Lightning invoice or address.",
  LIGHTNING_ADDRESS_NOT_YET_VALID: "Please enter a valid Lightning address",
  ADDRESS_INVALID_TLD: "That doesn't look like a valid Lightning address",
  LIGHTING_ADDRESS_IS_NOT_WORKING: "This Lightning address is not working",
  SOMETHING_WENT_WRONG_WITH_THIS_PAYMENT:
    "Something went wrong with this payment attempt.",
};

@model("exrails/PayAttempt")
export class PayAttempt extends Model({
  _version: prop(0),
  createdAtTimestamp: prop<number>(() => Date.now()),
  id: idProp,
  uuid: prop<string>(""),
  userEnteredPaymentString: prop<string>(""),
  payRequest: prop<string | undefined>(undefined),
  userFacingErrorMessage: prop<string | undefined>(undefined),
  payAttemptStatus: prop<PayAttemptStatus>("user-trying-to-specify"),
  summarizedInvoice: prop<Frozen<InvoiceSummary> | undefined>(undefined),
  payAmountSatoshis: prop<number | undefined>(undefined),
  userProposedLnurlPaymentAmount: prop<number | undefined>(undefined),
  userProposedLnurlPaymentComment: prop<string>(""),
  lnUrlOneResponse: prop<Frozen<LnUrlOneResponse> | undefined>(undefined),
  lnUrlPaymentAttemptInProgress: prop<boolean>(false),
  lnUrlPaymentAttemptStageTwoInProgress: prop<boolean>(false),
  advancedSettingsAreRevealed: prop<boolean>(false),
  maxRoutingFeeSatoshis: prop<number>(5),
  routingFeesPaidSatoshis: prop<number | undefined>(undefined),
  platformFeeSatoshis: prop<number | undefined>(undefined),
  waitingOnRailsToProvidePayAttemptUuid: prop<boolean>(false),
  paymentFailureErrorMessage: prop<string | undefined>(undefined),
  descriptionEncodedInInvoice: prop<string | undefined>(undefined),
  currencySnapshot: prop<Frozen<CurrencySnapshot> | undefined>(undefined),
  startOverIsNowRequired: prop<boolean>(false),
  finalLightningAddress: prop<string | undefined>(undefined),
  zapPictureUrl: prop<string | null>(null),
  zapName: prop<string | null>(null),
}) {
  @modelAction
  setStartOverIsNowRequired = (value: boolean) => {
    this.startOverIsNowRequired = value;
  };

  @modelAction
  setWaitingOnRailsToProvidePayAttemptUuid = (value: boolean) => {
    // Only log when value actually changes
    if (this.waitingOnRailsToProvidePayAttemptUuid !== value) {
      // console.log("[PAYMENT-FLOW] ⏳ Wait Rails:", value);
    }
    this.waitingOnRailsToProvidePayAttemptUuid = value;
  };

  @modelAction
  setCreatedAtTimestamp = (timestamp: number) => {
    this.createdAtTimestamp = timestamp;
  };

  @modelAction
  setStatus = (newStatus: PayAttemptStatus) => {
    // Only log when status actually changes
    if (this.payAttemptStatus !== newStatus) {
      // console.log(
      //   "[PAYMENT-FLOW] 📊 Status:",
      //   this.payAttemptStatus,
      //   "→",
      //   newStatus
      // );
    }
    this.payAttemptStatus = newStatus;
  };

  @modelAction
  setCurrencySnapshot = (snapshot: CurrencySnapshot) => {
    this.currencySnapshot = frozen(snapshot);
  };

  @modelAction
  setUuid = (uuid: string) => {
    this.uuid = uuid;
  };
  @computed
  get shouldShowMessageForRecipientFormField() {
    // Only show if we have an LNURL response and comment length > 0
    if (!this.lnUrlOneResponse?.data) return false;

    const commentLength = this.lnUrlOneResponse.data.commentLengthAllowed;
    return (
      commentLength !== undefined && commentLength !== null && commentLength > 0
    );
  }

  @computed
  get shouldShowPayNowButtonForInvoicePayment() {
    return (
      this.isReadyToPayViaRails &&
      !this.lnUrlPaymentAttemptInProgress &&
      !this.startOverIsNowRequired
    );
  }

  @computed
  get stagedPaymentAmountIsTooHigh() {
    if (this.payAmountSatoshis === undefined) return false;
    return (
      this.payAmountSatoshis + this.maxRoutingFeeSatoshis >
      this.bob.safeBalanceInSatoshis
    );
  }

  @computed
  get isReadyToPayViaRails() {
    return (
      this.payRequest &&
      this.payRequest.length > 0 &&
      this.payAmountSatoshis &&
      this.payAmountSatoshis > 0 &&
      !this.stagedPaymentAmountIsTooHigh &&
      this.maxRoutingFeeSatoshis > 0
    );
  }

  @computed
  get shouldShowErrorMessageInsteadOfPayButton() {
    // Show error if we have a valid invoice but can't pay due to insufficient balance
    return (
      this.payRequest &&
      this.payRequest.length > 0 &&
      this.payAmountSatoshis &&
      this.payAmountSatoshis > 0 &&
      this.stagedPaymentAmountIsTooHigh &&
      !this.lnUrlPaymentAttemptInProgress &&
      !this.startOverIsNowRequired &&
      !this.waitingOnRailsToProvidePayAttemptUuid
    );
  }

  @modelAction
  setAdvancedSettingsAreRevealed = (revealed: boolean) => {
    this.advancedSettingsAreRevealed = revealed;
  };

  @modelAction
  setMaxRoutingFeeSatoshis = (amount: number | undefined) => {
    if (amount === undefined || !Number.isFinite(amount) || amount < 0) {
      this.maxRoutingFeeSatoshis = 5; // default to 5 if invalid
      return;
    }
    this.maxRoutingFeeSatoshis = Math.floor(amount);
  };

  @computed
  get shouldShowTwoFactorInput() {
    if (!this.bob.workspace.accountRequiresTwoFactorFormForActions) {
      return false;
    }

    // Don't show 2FA input if payment is already in progress
    if (this.waitingOnRailsToProvidePayAttemptUuid) {
      return false;
    }

    // Don't show 2FA input if payment has already been created/processed
    if (STATUSES_SHOWING_SERVER_PERSISTENCE.includes(this.payAttemptStatus)) {
      return false;
    }

    // Don't show during LNURL loading states
    if (this.showLnurlPaymentLoader) {
      return false;
    }

    // Show for LNURL stage two form
    if (this.shouldShowLnurlStageTwoForm) {
      return true;
    }

    // Show for regular invoice payment
    if (this.shouldShowPayNowButtonForInvoicePayment) {
      return true;
    }

    return false;
  }

  @computed
  get shouldShowLnurlStageTwoForm() {
    return (
      this.payAttemptStatus === "needs-user-input-for-lnurl-stage-two" &&
      !this.startOverIsNowRequired
    );
  }

  @computed
  get shouldDisableLnurlStageTwoForm() {
    return (
      this.payAttemptStatus !== "needs-user-input-for-lnurl-stage-two" ||
      this.lnUrlPaymentAttemptStageTwoInProgress ||
      this.userProposedLnurlPaymentAmountIsToohigh
    );
  }

  @computed
  get shouldDisableLnurlStageTwoButton() {
    return (
      this.shouldDisableLnurlStageTwoForm ||
      !this.userProposedLnurlPaymentAmount ||
      this.userProposedLnurlPaymentAmount < 1
    );
  }

  @computed
  get shouldDisableLnrulStageTwoAmountInput() {
    return (
      this.payAttemptStatus !== "needs-user-input-for-lnurl-stage-two" ||
      this.lnUrlPaymentAttemptStageTwoInProgress
    );
  }

  @computed
  get shouldResetWhenLeavingSendBeforeAmount() {
    if (this.waitingOnRailsToProvidePayAttemptUuid) return false;
    if (this.lnUrlPaymentAttemptStageTwoInProgress) return false;
    if (STATUSES_SHOWING_SERVER_PERSISTENCE.includes(this.payAttemptStatus)) {
      return false;
    }

    if (this.lnUrlPaymentAttemptInProgress) return true;

    if (this.payAttemptStatus === "user-trying-to-specify") {
      return this.userEnteredPaymentString.trim().length > 0;
    }

    if (this.payAttemptStatus === "needs-user-input-for-lnurl-stage-two") {
      return !this.userProposedLnurlPaymentAmount;
    }

    if (this.payAttemptStatus === "needs-pay-amount") {
      return !this.payAmountSatoshis;
    }

    return false;
  }

  @observable
  lnUrlOneTimeoutId: NodeJS.Timeout | null = null;

  @modelAction
  setUserProposedLnurlPaymentComment = (comment) => {
    if (comment === undefined || comment.trim() === "") {
      this.userProposedLnurlPaymentComment = "";
      return;
    } else {
      this.userProposedLnurlPaymentComment = comment;
    }
  };

  @computed
  get userProposedLnurlPaymentAmountIsToohigh() {
    return (
      this.userProposedLnurlPaymentAmount + this.maxRoutingFeeSatoshis >
      this.bob.safeBalanceInSatoshis
    );
  }

  @modelAction
  setUserProposedLnurlPaymentAmount = (amount: number) => {
    // console.log("setUserProposedLnurlPaymentAmount called with", amount);
    if (amount === undefined || amount < 0) {
      this.userProposedLnurlPaymentAmount = undefined;
      return;
    }
    this.userProposedLnurlPaymentAmount = amount;
    setMaxRoutingFeeAutomatically(amount, this);
  };

  @computed
  get showLnurlPaymentLoader() {
    return (
      this.lnUrlPaymentAttemptInProgress ||
      this.lnUrlPaymentAttemptStageTwoInProgress
    );
  }

  @computed
  get shouldShowNumericKeypadForInvoice() {
    if (
      this.payAttemptStatus != "user-trying-to-specify" &&
      this.payAttemptStatus != "needs-user-input-for-lnurl-stage-two" &&
      this.payAttemptStatus != "ready-to-attempt" &&
      !this.startOverIsNowRequired
    ) {
      return true;
    }
    return false;
  }

  @computed
  get payAmountNumberInputShouldBeReadOnly() {
    if (this.summarizedInvoice?.data?.amountInSatoshis > 0) {
      // console.log("payAmountNumberInputShouldBeReadOnly is true");
      return true;
    }
    // console.log("payAmountNumberInputShouldBeReadOnly is false");
    return false;
  }

  @computed
  get showAdvancedButton() {
    return (
      this.hidePayattemptTextEntryForm &&
      !this.startOverIsNowRequired &&
      !this.waitingOnRailsToProvidePayAttemptUuid &&
      !this.showLnurlPaymentLoader &&
      !this.advancedSettingsAreRevealed
    );
  }

  @computed
  get showStartOverButton() {
    if (this.startOverIsNowRequired) {
      return true;
    }
    return (
      this.hidePayattemptTextEntryForm &&
      !this.waitingOnRailsToProvidePayAttemptUuid &&
      !this.showLnurlPaymentLoader
    );
  }

  @computed
  get hidePayattemptTextEntryForm() {
    return (
      this.payAttemptStatus !== "user-trying-to-specify" ||
      this.lnUrlPaymentAttemptInProgress ||
      this.lnUrlPaymentAttemptStageTwoInProgress
    );
  }

  @computed
  get showAmountStagedForPayment() {
    return this.hidePayattemptTextEntryForm && this.payAmountSatoshis;
  }

  @modelAction
  setPayAmountSatoshis = (amount) => {
    if (amount === undefined || amount < 1) {
      this.payAmountSatoshis = undefined;
      return;
    } else {
      this.payAmountSatoshis = amount;
      setMaxRoutingFeeAutomatically(amount, this);
      this.userFacingErrorMessage = undefined;
      if (this.summarizedInvoice?.data) {
        this.payAttemptStatus = "ready-to-attempt";
      }
    }
  };
  @modelAction
  tryToDecodeInvoice = () => {
    try {
      const decoded = decode(this.userEnteredPaymentString);
      // console.log("decoded invoice", decoded);
      this.payRequest = this.userEnteredPaymentString;
      const summarizedInvoice = summariseBolt11Invoice(decoded);
      this.summarizedInvoice = frozen(summarizedInvoice);

      const nowUnix = Math.floor(Date.now() / 1000);
      if (summarizedInvoice.expiresAt - nowUnix <= 10) {
        this.userFacingErrorMessage =
          "This invoice has expired. Please request a new invoice from your recipient.";
        return;
      }

      const invoiceValue = this.summarizedInvoice.data.amountInSatoshis;
      console.log(
        "invoice decoded, so now we should check if we will need a 2fa",
      );
      this.bob.checkIfWeNeedToShowTwoFactorForm();
      if (invoiceValue > 0) {
        this.setPayAmountSatoshis(invoiceValue);
        // console.log("got a valid value, set to ready to attempt");
        this.payAttemptStatus = "ready-to-attempt";
      } else {
        // console.log("invoice has no value, needs pay amount");
        this.payAttemptStatus = "needs-pay-amount";
      }
    } catch (e) {
      // console.log("could not decode invoice", e);
      Sentry.captureException(e, {
        tags: {
          class: "PayAttempt",
          function: "decodeInvoice",
          action: "invoice_decode",
        },
        contexts: {
          payment_details: {
            userEnteredPaymentString: this.userEnteredPaymentString,
            payAttemptStatus: this.payAttemptStatus,
            payRequest: this.payRequest,
            inputLength: this.userEnteredPaymentString?.length || 0,
          },
        },
      });
      this.userFacingErrorMessage = ERROR_MESSAGES.INVOICE_NOT_YET_VALID;
    }
  };

  @modelAction
  setUserEnteredPaymentString = (stringValue) => {
    this.userEnteredPaymentString = stringValue;
    const classification = classifyLightningInput(
      this.userEnteredPaymentString,
    );
    if (
      classification === "possible-lightning-address" ||
      classification === "possible-lnurl"
    ) {
      this.userFacingErrorMessage = undefined;
      this.handlePossibleLnurlDebounced();
    } else if (classification === "possible-lightning-invoice") {
      this.userFacingErrorMessage = undefined;
      this.tryToDecodeInvoice();
    } else if (classification === "address-shaped-invalid-tld") {
      this.userFacingErrorMessage = ERROR_MESSAGES.ADDRESS_INVALID_TLD;
    } else {
      this.userFacingErrorMessage = ERROR_MESSAGES.INVOICE_NOT_YET_VALID;
    }
  };

  @computed
  get bob() {
    return findParent<Bob>(this, (parentNode) => parentNode instanceof Bob);
  }

  @modelAction
  handlePossibleLnurlDebounced() {
    // console.log("handlepossibleLnurlDebounced called");
    // 1. Cancel any earlier timeout so only the last keystroke wins
    if (this.lnUrlOneTimeoutId) clearTimeout(this.lnUrlOneTimeoutId);

    // 2. Schedule a *lexical-arrow* callback (keeps `this`)
    this.lnUrlOneTimeoutId = setTimeout(() => {
      // Fire the flow; don’t await inside setTimeout
      void this.getLnurlOneDataFromRails();
    }, 500);
  }

  @modelFlow
  getLnurlOneDataFromRails = _async(function* (this: PayAttempt) {
    // we check for 2 factor form here because if this is a 2nd payment without page reload
    // we dont want to show the form
    this.bob.checkIfWeNeedToShowTwoFactorForm();
    this.lnUrlPaymentAttemptInProgress = true;
    const message = {
      bobUuid: this.bob.uuid,
      proposedLightningIdentifier: this.userEnteredPaymentString.trim(),
    };
    console.log("now we are getting lnurl data from rails", message);
    try {
      const response = yield* _await(
        sendToBobController(message, "/bob_life/get_lightning_address_info"),
      );
      console.log("got response from get lightning address info", response);

      if (!response?.success) {
        this.userFacingErrorMessage =
          ERROR_MESSAGES.LIGHTNING_ADDRESS_NOT_YET_VALID;
        this.lnUrlPaymentAttemptInProgress = false;
        return;
      }
      if (response.success) {
        this.lnUrlPaymentAttemptInProgress = false;
        const validation = validateLnUrlOneResponse(
          response.lightningAddressData,
        );

        if (validation.ok === true) {
          // ← TS now knows this branch is { ok: true; value: … }
          this.lnUrlOneResponse = frozen(response.lightningAddressData);
          this.payAttemptStatus = "needs-user-input-for-lnurl-stage-two";
          this.userFacingErrorMessage = undefined;
        } else {
          console.log("validation failed");
          // ← TS now knows this branch is { ok: false; error: string }
          this.userFacingErrorMessage = validation.error;
          return;
        }
      }
      // <-- update observable state here if you have a field for it
      // this.setLnurlData(response.lightningAddressData);
    } catch (err) {
      console.error("LNURL fetch failed", err);
      Sentry.captureException(err, {
        tags: {
          class: "PayAttempt",
          function: "loadLnurlStageOne",
          action: "lnurl_fetch",
        },
        contexts: {
          payment_details: {
            userEnteredPaymentString: this.userEnteredPaymentString,
            payAttemptStatus: this.payAttemptStatus,
            lnUrlPaymentAttemptInProgress: this.lnUrlPaymentAttemptInProgress,
          },
        },
      });
      this.lnUrlPaymentAttemptInProgress = false;
      return;
    }
  });

  @modelFlow
  continueLnUrlStageTwoPayment = _async(function* (
    this: PayAttempt,
    twoFactorCode: string,
  ) {
    this.lnUrlPaymentAttemptStageTwoInProgress = true;
    const message = {
      bobUuid: this.bob.uuid,
      rawCallbackUrl: this.lnUrlOneResponse.data.rawCallBackUrl,
      satoshisAmount: this.userProposedLnurlPaymentAmount,
      comment: this.userProposedLnurlPaymentComment?.trim() || null,
    };
    console.log("now getting lnurl data from rails", message);
    try {
      const response = yield* _await(
        sendToBobController(message, "/bob_life/get_lightning_address_invoice"),
      );

      console.log("got response from get lightning address info", response);

      if (!response?.success) {
        this.lnUrlPaymentAttemptStageTwoInProgress = false;
        this.userFacingErrorMessage =
          ERROR_MESSAGES.LIGHTING_ADDRESS_IS_NOT_WORKING;
        return;
      }
      if (response.success) {
        console.log(
          "now we need to test the htag and amount and pay this invoice",
          response,
        );
        this.handleResponseFromLnurlTwo(response, twoFactorCode);
      }
      // <-- update observable state here if you have a field for it
      // this.setLnurlData(response.lightningAddressData);
    } catch (err) {
      console.error("LNURL fetch failed", err);
      Sentry.captureException(err, {
        tags: {
          class: "PayAttempt",
          function: "loadLnurlStageTwo",
          action: "lightning_address_invoice",
        },
        contexts: {
          payment_details: {
            userEnteredPaymentString: this.userEnteredPaymentString,
            userProposedLnurlPaymentAmount: this.userProposedLnurlPaymentAmount,
            userProposedLnurlPaymentComment:
              this.userProposedLnurlPaymentComment,
            lnUrlPaymentAttemptStageTwoInProgress:
              this.lnUrlPaymentAttemptStageTwoInProgress,
          },
        },
      });
    }
  });

  @modelAction
  handleResponseFromLnurlTwo(response, twoFactorCode) {
    console.log(
      "[LNURL] → handleResponseFromLnurlTwo called with response:",
      response,
    );

    try {
      const payRequest = response.lightningInvoiceData.payRequest;
      console.log("[LNURL] → payRequest string extracted:", payRequest);

      const decodedPayRequest = decode(payRequest);
      console.log(
        "[LNURL] → payRequest successfully decoded:",
        decodedPayRequest,
      );

      const descHashSection = decodedPayRequest.sections.find(
        (s) => s.name === "description_hash",
      );

      if (descHashSection && descHashSection.value) {
        const invoiceDescHashHex = uint8ArrayToHex(
          descHashSection.value,
        ).toLowerCase();
        const expectedHashHex =
          this.lnUrlOneResponse.data.hTagInInvoiceMustMatch.toLowerCase();

        console.log("[LNURL] → invoiceDescHashHex:", invoiceDescHashHex);
        console.log(
          "[LNURL] → expectedHashHex from LNURL metadata:",
          expectedHashHex,
        );

        if (invoiceDescHashHex !== expectedHashHex) {
          console.error(
            "[LNURL] ❌ description_hash mismatch:",
            invoiceDescHashHex,
            "≠",
            expectedHashHex,
          );
          this.userFacingErrorMessage =
            "The invoice does not match the LNURL metadata (h-tag mismatch).";
          this.lnUrlPaymentAttemptStageTwoInProgress = false;
          return;
        }

        console.log("[LNURL] ✅ h-tag verified successfully");
      } else {
        console.warn(
          "[LNURL] ⚠️ Invoice has no description_hash section – skipping h-tag check. This is valid if invoice contains a short description ('d' tag).",
        );
      }

      const summarizedInvoice = summariseBolt11Invoice(decodedPayRequest);
      console.log("[LNURL] → summarized invoice:", summarizedInvoice);

      const nowUnix = Math.floor(Date.now() / 1000);
      if (summarizedInvoice.expiresAt - nowUnix <= 10) {
        this.lnUrlPaymentAttemptStageTwoInProgress = false;
        this.userFacingErrorMessage =
          "This invoice has expired. Please request a new invoice from your recipient.";
        return;
      }

      this.summarizedInvoice = frozen(summarizedInvoice);
      const invoiceValue = this.summarizedInvoice.data.amountInSatoshis;
      console.log("[LNURL] → invoiceValue extracted:", invoiceValue);

      this.payRequest = payRequest;

      if (invoiceValue !== this.userProposedLnurlPaymentAmount) {
        console.error(
          "[LNURL] ❌ invoice amount mismatch:",
          invoiceValue,
          "≠",
          this.userProposedLnurlPaymentAmount,
        );
        this.lnUrlPaymentAttemptStageTwoInProgress = false;
        this.userFacingErrorMessage =
          "The invoice amount does not match the LNURL metadata (amount mismatch).";
        return;
      }

      console.log(
        "[LNURL] ✅ amount matches, preparing to pay invoice via Rails",
      );
      this.payAmountSatoshis = invoiceValue;
      this.payAttemptStatus = "ready-to-attempt";

      console.log(
        "[LNURL] → pay attempt snapshot before sending to Rails:",
        getSnapshot(this),
      );
      this.lnUrlPaymentAttemptStageTwoInProgress = false;

      this.tryToPayInvoiceViaRails(twoFactorCode);
    } catch (e) {
      this.lnUrlPaymentAttemptStageTwoInProgress = false;
      console.error(
        "[LNURL] ❌ Exception thrown in handleResponseFromLnurlTwo:",
        e,
      );
      Sentry.captureException(e, {
        tags: {
          class: "PayAttempt",
          function: "handleResponseFromLnurlTwo",
          action: "lnurl_response_handling",
        },
        contexts: {
          payment_details: {
            userEnteredPaymentString: this.userEnteredPaymentString,
            userProposedLnurlPaymentAmount: this.userProposedLnurlPaymentAmount,
            payRequest: this.payRequest,
            payAmountSatoshis: this.payAmountSatoshis,
            lnUrlOneResponse: this.lnUrlOneResponse,
          },
        },
      });
      this.userFacingErrorMessage = "Error decoding LNURL invoice";
    }
  }

  @computed
  get isUserEnteredPaymentStringALightningAddress() {
    return (
      this.userEnteredPaymentString &&
      this.userEnteredPaymentString.includes("@")
    );
  }

  @modelFlow
  tryToPayInvoiceViaRails = _async(function* (
    this: PayAttempt,
    twoFactorCode: string | null,
  ) {
    // console.log(
    //   "[PAYMENT-FLOW] 🚀 Payment start:",
    //   this.payAmountSatoshis,
    //   "sats"
    // );

    const finalLightningAddress = this
      .isUserEnteredPaymentStringALightningAddress
      ? this.userEnteredPaymentString.trim()
      : null;
    if (finalLightningAddress) {
      this.finalLightningAddress = finalLightningAddress;
    }

    const message = {
      bobUuid: this.bob.uuid,
      payRequest: this.payRequest,
      payAmountSatoshis: this.payAmountSatoshis,
      maxRoutingFeeSatoshis: this.maxRoutingFeeSatoshis,
      comment: this.userProposedLnurlPaymentComment?.trim() || null,
      twoFactorCode: twoFactorCode.length > 0 ? twoFactorCode : null, // <-- new=
      lightningAddress: finalLightningAddress,
    };
    console.log("now asking rails to pay this", message);
    console.log(
      "rails should parse the invoice and be sure the payment amount matches or that the invoice has no amount",
    );
    try {
      // console.log(
      //   "[PAYMENT-FLOW] ⏳ About to set waiting on rails to true, current balance:",
      //   this.bob.safeBalanceInSatoshis
      // );
      this.setWaitingOnRailsToProvidePayAttemptUuid(true);
      const response = yield* _await(
        sendToBobController(message, "/bob_payattempt/attempt_payment"),
      );

      //console.log("got response from pay attempt controller", response);

      if (!response?.success) {
        console.log("response was not successful", response);
        this.userFacingErrorMessage = response.message;
        this.setWaitingOnRailsToProvidePayAttemptUuid(false);
        this.setStartOverIsNowRequired(true);
        return;
      }
      if (response.success) {
        console.log(
          "this response should have a uuid which we can use the track this payment attempt",
          response,
        );
        this.setUuid(response.uuid);
        this.bob.workspace.makeEvent(
          "Payment attempt started",
          `We've started a payment attempt in the amount of ${this.payAmountSatoshis.toLocaleString()} satoshis`,
          "payattempt-started",
          this.payRequest,
          response.uuid,
        );

        this.setCurrencySnapshot(response.bob_currency_snapshot);
        this.setStatus("created");
        console.log(
          "we just set this status to created so it should show up in the transactions list",
          getSnapshot(this),
        );

        // dont need to do  this.setWaitingOnRailsToProvidePayAttemptUuid(false) cuz we refresh this one
        //this.bob.refreshPayAttempt();
      }
      // <-- update observable state here if you have a field for it
      // this.setLnurlData(response.lightningAddressData);
    } catch (err) {
      this.setWaitingOnRailsToProvidePayAttemptUuid(false);
      this.userFacingErrorMessage =
        ERROR_MESSAGES.SOMETHING_WENT_WRONG_WITH_THIS_PAYMENT;
      console.error("Payment attempt failed", err);
      Sentry.captureException(err, {
        tags: {
          class: "PayAttempt",
          function: "tryToPayInvoiceViaRails",
          action: "payment_attempt",
        },
        contexts: {
          payment_details: {
            payAmountSatoshis: this.payAmountSatoshis,
            userEnteredPaymentString: this.userEnteredPaymentString,
            payRequest: this.payRequest,
            payAttemptStatus: this.payAttemptStatus,
            waitingOnRailsToProvidePayAttemptUuid:
              this.waitingOnRailsToProvidePayAttemptUuid,
            uuid: this.uuid,
          },
        },
      });
    }
  });

  // @computed
  // get simplifiedStatus(): string {
  //   if (this.payAttemptStatus === "created") {
  //     return "created";
  //   } else if (this.payAttemptStatus === "has_failed") {
  //     return "failed";
  //   } else if (this.payAttemptStatus === "has_succeeded") {
  //     return "paid";
  //   } else {
  //     return "in progress";
  //   }
  // }

  @computed
  get spreadsheetLine() {
    return makeSpreadsheetLine({
      type: "Lightning Payment",
      status: this.makeTransactionLabel,
      uuid: this.uuid,
      createdAtInteger: this.createdAtTimestamp,
      currencySnapshot: this.currencySnapshot.data,
      currency: this.bob.currencyCode,
      amountSatoshis: this.payAmountSatoshis || 0,
      isCredit: false,
      comment: this.userProposedLnurlPaymentComment?.trim() || null,
      payRequest: this.payRequest || null,
      routingFeeSatoshis: this.routingFeesPaidSatoshis,
      platformFeeSatoshis: this.platformFeeSatoshis,
      lightningAddress: this.finalLightningAddress || null,
    });
  }

  // export type PayAttemptStatus =
  //   | "user-trying-to-specify"
  //   | "needs-user-input-for-lnurl-stage-two"
  //   | "needs-pay-amount"
  //   | "ready-to-attempt"
  //   | "created"
  //   | "has_failed"
  //   | "has_succeeded";
  // @computed
  // get makeNiceLookingSimplifiedStatus() {
  //   switch (this.simplifiedStatus) {
  //     case "created":
  //       return "Send Initiated";
  //     case "has_failed":
  //       return "Failed Send";
  //     case "has_succeeded":
  //       return "Sent";
  //     default:
  //       return "Contact Support with PA: " + this.uuid;
  //   }
  // }

  // @computed
  get makeTransactionLabel() {
    switch (this.payAttemptStatus) {
      case "created":
        return "Send Initiated";
      case "has_failed":
        return "Failed Send";
      case "has_succeeded":
        return "Sent";
      default:
        return "Contact Support with PA: " + this.uuid;
    }
  }

  @computed
  get transactionsListLine() {
    return makeTransactionsListLine({
      amountSatoshis: this.payAmountSatoshis,
      label: this.makeTransactionLabel,
      createdAtInteger: this.createdAtTimestamp,
      comment: this.userProposedLnurlPaymentComment?.trim() || null,
      currencySnapshot: this.currencySnapshot.data,
      currency: this.bob.currencyCode,
      isCredit: false,
      modelName: "PayAttempt",
      uuid: this.uuid,
      isConfirmed: this.payAttemptStatus === "has_succeeded",
      lightningAddress: this.finalLightningAddress,
      zapName: this.zapName,
      zapPictureUrl: this.zapPictureUrl,
    });
  }
  @computed
  get transactionPageData() {
    return makeTransactionsPage({
      header: "Sent",
      uuid: this.uuid,
      createdAtInteger: this.createdAtTimestamp,
      currencySnapshot: this.currencySnapshot.data,
      currency: this.bob.currencyCode,
      amountSatoshis: this.payAmountSatoshis,
      isCredit: false,
      comment: this.userProposedLnurlPaymentComment?.trim() || null,
      payRequest: this.payRequest || null,
      routingFeeSatoshis: this.routingFeesPaidSatoshis,
      platformFeeSatoshis: this.platformFeeSatoshis,
      lightningAddress: this.finalLightningAddress || null,
    });
  }
}

export interface PayAttemptInterface {
  PayAttempt: PayAttempt;
}

export const PayAttemptRef = rootRef<PayAttempt>("exrails/PayAttemptRef", {
  onResolvedValueChange(ref, newThing, oldThing) {
    if (newThing && !oldThing) {
      detach(ref);
    }
  },
});
